import { Checkbox, InputNumber, TimePicker } from 'antd'
import classNames from 'classnames'
import dayjs, { type Dayjs } from 'dayjs'
import type { FC } from 'react'

import { DatePicker } from 'common/components/DatePicker/DatePicker'
import { Label } from 'common/components/Label/Label'
import { Select } from 'common/components/Select/Select'
import { TIME_FORMAT } from 'common/constants/timeFormat'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'

import styles from './ehrAppointmentV2.module.scss'

type Props = {
  date: Dayjs
  time: Dayjs
  duration: number
  room: string
  rooms: IPatientRoom[]
  maxDuration: number
  note: string
  disabled: boolean
  timeError?: string
  showBypass?: boolean
  bypass?: boolean
  roomError?: string
  isDateDisabled?: (date: Dayjs) => boolean
  onDateChange?: (date: Dayjs) => void
  onTimeChange: (time: Dayjs) => void
  onDurationChange: (duration: number) => void
  onRoomChange: (room: string) => void
  onBypassChange?: (bypass: boolean) => void
}

export const VisitRow: FC<Props> = ({
  date,
  time,
  duration,
  room,
  rooms,
  maxDuration,
  note,
  disabled,
  timeError,
  showBypass,
  bypass,
  roomError,
  isDateDisabled,
  onDateChange,
  onTimeChange,
  onDurationChange,
  onRoomChange,
  onBypassChange,
}) => (
  <div className={classNames(styles.band, styles.bandTight)}>
    <div className={styles.visitTag}>
      <span className={classNames(styles.visitDot, styles.dotVisit1)} />
      <span>Visit</span>
    </div>

    <div className={classNames(styles.field, styles.widthDate)}>
      <DatePicker
        label='Date'
        allowClear={false}
        value={date}
        disabled={disabled}
        disabledDate={isDateDisabled}
        format='MM-DD-YYYY'
        showNow={false}
        onChange={(value) => value && onDateChange?.(value)}
      />
    </div>

    <div className={styles.field}>
      <Label label='Time' id='visit-time' />
      <TimePicker
        id='visit-time'
        aria-label='Appointment time'
        className={styles.timePicker}
        value={time}
        allowClear={false}
        disabled={disabled}
        format={TIME_FORMAT.TWELVE_HOUR_FORMAT}
        minuteStep={5}
        needConfirm={false}
        onChange={(value) => value && onTimeChange(value)}
      />
    </div>

    <div className={styles.field}>
      <Label label='Duration' id='visit-duration' />
      <InputNumber
        id='visit-duration'
        aria-label='Duration in minutes'
        className={styles.durationInput}
        value={duration}
        min={1}
        max={maxDuration}
        disabled={disabled}
        onChange={(value) => onDurationChange(Math.max(1, Math.min(value || 1, maxDuration)))}
      />
    </div>

    <div className={classNames(styles.field, styles.widthSelect)}>
      <Select
        label='Room'
        required
        value={room || undefined}
        disabled={disabled}
        showSearch={false}
        options={rooms.map((item) => ({
          value: item.value,
          label: item.short_name ?? item.label,
        }))}
        onChange={onRoomChange}
        error={roomError}
      />
    </div>

    {showBypass && (
      <div className={styles.bypassCheck}>
        <Checkbox
          checked={bypass}
          disabled={disabled}
          onChange={(event) => onBypassChange?.(event.target.checked)}>
          Bypass temperature check
        </Checkbox>
      </div>
    )}

    <div className={styles.fieldGrow} />
    {timeError ? (
      <span className={styles.errorText}>{timeError}</span>
    ) : (
      <span className={styles.endNote}>{note}</span>
    )}
  </div>
)

export const visitEndNote = (time: Dayjs, duration: number): string =>
  `Ends ${dayjs(time).add(duration, 'minutes').format(TIME_FORMAT.TWELVE_HOUR_FORMAT)}`
